import i18n from "i18next";
/**
 * Page & settings on a phone (Olivia, 10 Oct): every setting is a tap, never a
 * keyboard (the user: "no keyboard usage except for Ask Lilia and maybe doc
 * title change"). Free values (custom margins, header and footer text, the
 * class and its options) stay on desktop; the phone shows them as read-only.
 */

export interface DocSettingsValues {
  title: string;
  paperSize?: string | null;
  columns?: number | null;
  fontSize?: number | null;
  marginTop?: string | null;
  marginBottom?: string | null;
  marginLeft?: string | null;
  marginRight?: string | null;
  lineSpacing?: number | null;
  paragraphIndent?: string | null;
  pageNumbering?: string | null;
  /** Numbered heading levels: null = the class default, 0 = none, 1–5. */
  headingNumbering?: number | null;
  language?: string | null;
  /** With language "auto": what the text reads as (API #350). */
  detectedLanguage?: string | null;
  /** Read-only on a phone. */
  documentClass?: string | null;
  classOptions?: string | null;
  headerText?: string | null;
  footerText?: string | null;
}

/** What a change sends to the API (PUT /documents/:id): the fields as the desktop dialog writes them. */
export type DocSettingsPatch = Partial<
  Pick<DocSettingsValues, "paperSize" | "columns" | "fontSize" | "marginTop" | "marginBottom" | "marginLeft" | "marginRight" | "lineSpacing" | "paragraphIndent" | "pageNumbering" | "language">
> & {
  /** -1 puts the class default back. */
  headingNumbering?: number;
};

export interface Choice<T extends string | number> {
  value: T;
  label: string;
}

export const PAPER: Choice<string>[] = [
  { value: "a4", label: "A4" },
  { value: "letter", label: "Letter" },
  { value: "a5", label: "A5" },
  { value: "legal", label: "Legal" },
];
export const COLUMNS: Choice<number>[] = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
];
export const FONT_SIZES: Choice<number>[] = [
  { value: 10, label: "10 pt" },
  { value: 11, label: "11 pt" },
  { value: 12, label: "12 pt" },
];
export const LINE_SPACING: Choice<string>[] = [
  { value: "1", label: "1" },
  { value: "1.15", label: "1.15" },
  { value: "1.5", label: "1.5" },
  { value: "2", label: "2" },
];
export const INDENT: Choice<string>[] = [
  { value: "none", label: "None" },
  { value: "1em", label: "1 em" },
  { value: "1.5em", label: "1.5 em" },
  { value: "2em", label: "2 em" },
];
export const PAGE_NUMBERS: Choice<string>[] = [
  { value: "arabic", label: "1 2 3" },
  { value: "roman", label: "i ii iii" },
  { value: "none", label: "None" },
];
/** Numbered heading levels; -1 is the class default. Four and five levels are on desktop. */
export const HEADING_NUMBERS: Choice<number>[] = [
  { value: -1, label: "Default" },
  { value: 0, label: "None" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
];

/** Margins as Narrow · Normal · Wide: the same on all four sides. */
export const MARGINS: Choice<"narrow" | "normal" | "wide">[] = [
  { value: "narrow", label: "Narrow" },
  { value: "normal", label: "Normal" },
  { value: "wide", label: "Wide" },
];
export const MARGIN_SIZE: Record<"narrow" | "normal" | "wide", string> = { narrow: "1.5cm", normal: "2.5cm", wide: "3.5cm" };

/** The document's language, which its PDF is typeset in (API #350). "auto": read from the text;
 *  its label is the interface's word for it (languageLabel). The others are named in their own language. */
export const LANGUAGES: Choice<string>[] = [
  { value: "auto", label: "" },
  { value: "en", label: "English" },
  { value: "en-GB", label: "English (UK)" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "es", label: "Español" },
  { value: "it", label: "Italiano" },
  { value: "pt", label: "Português" },
  { value: "nl", label: "Nederlands" },
  { value: "pl", label: "Polski" },
  { value: "sv", label: "Svenska" },
  { value: "tr", label: "Türkçe" },
  { value: "ru", label: "Русский" },
  { value: "el", label: "Ελληνικά" },
  { value: "ar", label: "العربية" },
  { value: "he", label: "עברית" },
  { value: "zh", label: "中文" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
];

/**
 * The margins as a preset: Normal when none are set (the class's own), the
 * preset when all four match one, otherwise "custom" (set on desktop).
 */
export function marginPreset(v: DocSettingsValues): "narrow" | "normal" | "wide" | "custom" {
  const sides = [v.marginTop, v.marginBottom, v.marginLeft, v.marginRight].map((s) => (s ?? "").replace(/\s+/g, "").toLowerCase());
  if (sides.every((s) => !s)) return "normal";
  for (const [name, size] of Object.entries(MARGIN_SIZE) as Array<["narrow" | "normal" | "wide", string]>) {
    if (sides.every((s) => s === size)) return name;
  }
  return "custom";
}

/** A preset's patch: all four sides. */
export function marginPatch(preset: "narrow" | "normal" | "wide"): DocSettingsPatch {
  const s = MARGIN_SIZE[preset];
  return { marginTop: s, marginBottom: s, marginLeft: s, marginRight: s };
}

/** The patch that puts back what a patch changed. */
export function revertPatch(before: DocSettingsValues, patch: DocSettingsPatch): DocSettingsPatch {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(patch) as Array<keyof DocSettingsPatch>)
    out[k] = k === "headingNumbering" ? (before.headingNumbering ?? -1) : (before[k] ?? defaultFor(k));
  return out as DocSettingsPatch;
}

/** What an unset field is, for putting it back: the API keeps a value it is not sent. */
function defaultFor(k: Exclude<keyof DocSettingsPatch, "headingNumbering">): string | number {
  switch (k) {
    case "paperSize":
      return "a4";
    case "columns":
      return 1;
    case "fontSize":
      return 11;
    case "lineSpacing":
      return 1;
    case "pageNumbering":
      return "arabic";
    case "language":
      return "en";
    case "paragraphIndent":
      return "1.5em";
    default:
      return MARGIN_SIZE.normal;
  }
}

/** The label of a value in a list, or the value itself. */
export function labelOf<T extends string | number>(choices: Choice<T>[], value: T | null | undefined): string {
  const label = choices.find((c) => String(c.value) === String(value))?.label;
  return label != null ? sayChoice(label) : value == null || value === "" ? i18n.t("mobile:settings.default") : String(value);
}

/** The few choice labels that are words ("Letter", "None", "Wide"); numbers and language names stay as they are. */
const WORDS = new Set(["Default", "Letter", "Legal", "None", "Narrow", "Normal", "Wide"]);
export function sayChoice(label: string): string {
  return WORDS.has(label) ? i18n.t(`mobile:settings.words.${label}`) : label;
}

/** A language's name: its own, or for "auto" the interface's word, with what the text reads as. */
export function languageLabel(value: string | null | undefined, autoWord: string, detected?: string | null): string {
  const v = value || "auto";
  if (v !== "auto") return LANGUAGES.find((l) => l.value === v)?.label ?? v;
  const found = detected ? LANGUAGES.find((l) => l.value === detected)?.label : null;
  return found ? `${autoWord} · ${found}` : autoWord;
}
